import React, { createContext, useContext, useState, useEffect } from 'react';
import { timeTrackingApi } from '../services/timeTrackingApi';
import useOfflineSync from '../hooks/useOfflineSync';
import offlineTrackingService from '../services/offlineTrackingService';

const TimeTrackingContext = createContext();

export const useTimeTracking = () => {
  const context = useContext(TimeTrackingContext);
  if (!context) {
    throw new Error('useTimeTracking must be used within TimeTrackingProvider');
  }
  return context;
};

export const TimeTrackingProvider = ({ children }) => {
  const [sessionId, setSessionId] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTask, setSelectedTask] = useState('');
  const [projectName, setProjectName] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [initialized, setInitialized] = useState(false);
  
  // Idle detection state
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [totalIdleTime, setTotalIdleTime] = useState(0);
  const [isCurrentlyIdle, setIsCurrentlyIdle] = useState(false);
  
  const IDLE_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds
  const ACTIVITY_CHECK_INTERVAL = 30 * 1000; // Check every 30 seconds
  
  // Offline sync functionality
  const { isOnline, pendingUpdates, queueTrackingUpdate, queueStopTracking } = useOfflineSync();

  // Activity detection effect
  useEffect(() => {
    if (!isTracking) return;
    
    const handleActivity = () => {
      const now = Date.now();
      setLastActivity(now);
      if (isCurrentlyIdle) {
        console.log('User became active again');
        setIsCurrentlyIdle(false);
      }
    };
    
    // Add activity listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'click', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [isTracking, isCurrentlyIdle]);

  // Initialize from database on load
  useEffect(() => {
    const initializeFromDatabase = async () => {
      try {
        // Only initialize if user is authenticated
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('No token found, skipping session restore');
          setInitialized(true);
          return;
        }
        
        console.log('Attempting to restore active session...');
        const response = await timeTrackingApi.getActiveSession();
        console.log('Active session response:', response.data);
        
        if (response.data.session) {
          const session = response.data.session;
          const currentTime = Date.now();
          const sessionStartTime = response.data.start_time_timestamp;
          
          console.log('Restoring session:', {
            sessionId: session.id,
            projectName: session.project_name,
            taskTitle: session.task_title,
            startTime: sessionStartTime,
            currentTime
          });
          
          setSessionId(session.id);
          setIsTracking(true);
          setSelectedProject(session.project_id?.toString() || '');
          setSelectedTask(session.task_id?.toString() || '');
          setProjectName(session.project_name || '');
          setTaskTitle(session.task_title || '');
          setStartTime(sessionStartTime);
          
          // Calculate current elapsed time from session start
          const currentElapsed = Math.floor((currentTime - sessionStartTime) / 1000);
          setElapsedTime(currentElapsed);
          
          // Restore idle time data
          setTotalIdleTime(session.idle_seconds || 0);
          setLastActivity(currentTime - 60000); // Assume last activity was 1 minute ago
          setIsCurrentlyIdle(false);
          
          console.log('Session restored successfully, elapsed time:', currentElapsed);
        } else {
          console.log('No active session found');
          
          // Check offline storage for active session
          const offlineSession = offlineTrackingService.getActiveSession();
          if (offlineSession) {
            console.log('Restoring session from offline storage:', offlineSession);
            setSessionId(offlineSession.sessionId);
            setIsTracking(true);
            setSelectedProject(offlineSession.selectedProject || '');
            setSelectedTask(offlineSession.selectedTask || '');
            setProjectName(offlineSession.projectName || '');
            setTaskTitle(offlineSession.taskTitle || '');
            setStartTime(offlineSession.startTime);
            setElapsedTime(offlineSession.elapsedTime || 0);
            setTotalIdleTime(offlineSession.totalIdleTime || 0);
            setLastActivity(Date.now() - 60000);
            setIsCurrentlyIdle(false);
          }
        }
      } catch (error) {
        console.error('Failed to restore active session:', error);
      } finally {
        setInitialized(true);
      }
    };
    
    // Add a small delay to ensure API is ready
    setTimeout(initializeFromDatabase, 100);
  }, []);

  // Global timer effect with idle detection
  useEffect(() => {
    let interval;
    if (isTracking && startTime && initialized) {
      interval = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedTime(elapsed);
        
        // Check for idle status
        const timeSinceActivity = now - lastActivity;
        const wasIdle = isCurrentlyIdle;
        const nowIdle = timeSinceActivity > IDLE_THRESHOLD;
        
        if (nowIdle !== wasIdle) {
          setIsCurrentlyIdle(nowIdle);
          if (nowIdle) {
            console.log('User became idle');
          } else {
            console.log('User became active');
          }
        }
        
        // Add to idle time if currently idle
        if (nowIdle) {
          setTotalIdleTime(prev => prev + 1);
        }
        
        // Update tracking every 30 seconds
        if (elapsed % 30 === 0 && sessionId) {
          const activeTime = elapsed - totalIdleTime - (nowIdle ? Math.floor(timeSinceActivity / 1000) : 0);
          const finalIdleTime = totalIdleTime + (nowIdle ? Math.floor(timeSinceActivity / 1000) : 0);
          
          // Save to offline storage
          offlineTrackingService.saveActiveSession({
            sessionId,
            startTime,
            elapsedTime: elapsed,
            totalIdleTime: finalIdleTime,
            activeTime: Math.max(0, activeTime),
            projectName,
            taskTitle,
            selectedProject,
            selectedTask
          });
          
          console.log('Updating tracking:', {
            total: elapsed,
            active: Math.max(0, activeTime),
            idle: finalIdleTime,
            online: isOnline
          });
          
          // Use offline-capable queue instead of direct API call
          queueTrackingUpdate(
            sessionId, 
            elapsed, 
            Math.max(0, activeTime), 
            finalIdleTime
          );
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTracking, startTime, sessionId, initialized, lastActivity, totalIdleTime, isCurrentlyIdle]);

  const startTracking = async (projectId, taskId) => {
    try {
      console.log('TimeTrackingContext: Starting tracking...', { projectId, taskId });
      
      // Get project and task names
      const api = require('../services/api').default;
      const [projectResponse, taskResponse] = await Promise.all([
        projectId ? api.get(`/projects/${projectId}`) : Promise.resolve({ data: null }),
        taskId ? api.get(`/tasks/${taskId}`) : Promise.resolve({ data: null })
      ]);
      
      const response = await timeTrackingApi.startTracking(projectId, taskId);
      console.log('TimeTrackingContext: Start tracking response:', response.data);
      
      if (response.data.success) {
        const newSessionId = response.data.session_id;
        const newStartTime = Date.now();
        const projectName = projectResponse.data?.name || '';
        
        setSessionId(newSessionId);
        setIsTracking(true);
        setSelectedProject(projectId?.toString() || '');
        setSelectedTask(taskId?.toString() || '');
        setProjectName(projectName);
        setTaskTitle(taskResponse.data?.title || '');
        setStartTime(newStartTime);
        setElapsedTime(0);
        setLastActivity(newStartTime);
        setTotalIdleTime(0);
        setIsCurrentlyIdle(false);
        
        // Notify Electron app about tracking start with project and task info
        if (window.electronAPI) {
          await window.electronAPI.startTracking(newSessionId, projectId, projectName, taskId);
        }
        
        console.log('TimeTrackingContext: Tracking started successfully');
      }
      return response.data;
    } catch (error) {
      console.error('TimeTrackingContext: Failed to start tracking:', error);
      throw error;
    }
  };

  const stopTracking = async () => {
    try {
      if (sessionId) {
        // Send final update with current idle time before stopping
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        const timeSinceActivity = now - lastActivity;
        const currentlyIdle = timeSinceActivity > IDLE_THRESHOLD;
        const finalIdleTime = totalIdleTime + (currentlyIdle ? Math.floor(timeSinceActivity / 1000) : 0);
        const finalActiveTime = elapsed - finalIdleTime;
        
        console.log('Final tracking update before stop:', {
          total: elapsed,
          active: Math.max(0, finalActiveTime),
          idle: finalIdleTime,
          online: isOnline
        });
        
        // Use offline-capable queue for final update
        queueTrackingUpdate(
          sessionId, 
          elapsed, 
          Math.max(0, finalActiveTime), 
          finalIdleTime
        );
        
        // Use offline-capable stop tracking
        await queueStopTracking(sessionId);
        
        // Stop Electron tracking
        if (window.electronAPI) {
          await window.electronAPI.stopTracking();
        }
        
        setSessionId(null);
        setIsTracking(false);
        setSelectedProject('');
        setSelectedTask('');
        setProjectName('');
        setTaskTitle('');
        setStartTime(null);
        setElapsedTime(0);
        setLastActivity(Date.now());
        setTotalIdleTime(0);
        setIsCurrentlyIdle(false);
        
        // Clear offline storage when stopping
        offlineTrackingService.clearActiveSession();
      }
    } catch (error) {
      console.error('Failed to stop tracking:', error);
      throw error;
    }
  };

  const value = {
    sessionId,
    isTracking,
    selectedProject,
    selectedTask,
    projectName,
    taskTitle,
    startTime,
    elapsedTime,
    totalIdleTime,
    isCurrentlyIdle,
    isOnline,
    pendingUpdates,
    startTracking,
    stopTracking
  };

  return (
    <TimeTrackingContext.Provider value={value}>
      {children}
    </TimeTrackingContext.Provider>
  );
};